"use client";

import { useState, useEffect } from "react";

const InteractiveSubtitleDemo = () => {
  const [selectedColor, setSelectedColor] = useState("white");
  const [selectedBgColor, setSelectedBgColor] = useState("red-500");
  const [selectedFontStyle, setSelectedFontStyle] = useState("modern-sans");
  const [selectedFormat, setSelectedFormat] = useState("SRT");

  const getTextColorClass = (color: string) => {
    switch (color) {
      case "white":
        return "text-white";
      case "yellow-400":
        return "text-yellow-400";
      case "cyan-400":
        return "text-cyan-400";
      default:
        return "text-white";
    }
  };

  const getBgColorClass = (color: string) => {
    switch (color) {
      case "red-500":
        return "bg-red-500/70";
      case "yellow-300":
        return "bg-yellow-300/70";
      case "blue-500":
        return "bg-blue-500/70";
      default:
        return "bg-black/70";
    }
  };

  const getFontFamilyClass = (fontStyle: string) => {
    switch (fontStyle) {
      case "modern-sans":
        return "font-['Inter']";
      case "classic-serif":
        return "font-['Playfair_Display']";
      case "bold-display":
        return "font-['Bebas_Neue']";
      default:
        return "font-['Inter']";
    }
  };

  useEffect(() => {
    // Load Google Fonts
    const fontLinkId = "google-fonts-subtitle-demo";
    
    // Check if font link already exists
    if (!document.getElementById(fontLinkId)) {
      const fontLink = document.createElement("link");
      fontLink.id = fontLinkId;
      fontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Playfair+Display:wght@400;700&family=Bebas+Neue&display=swap";
      fontLink.rel = "stylesheet";
      fontLink.crossOrigin = "anonymous";
      document.head.appendChild(fontLink);
    }

    // No cleanup needed - fonts can stay loaded for performance
  }, []);

  return (
    <>
      {/* Video player with subtitles */}
      <div className="relative aspect-video bg-gray-900 mb-4 rounded-lg overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          Video Preview
        </div>

        {/* Subtitles display */}
        <div className="absolute bottom-3rem left-0 right-0 px-4">
          <div className={`${getBgColorClass(selectedBgColor)} backdrop-blur-sm rounded-lg p-3 text-center`}>
            <p className={`${getTextColorClass(selectedColor)} ${getFontFamilyClass(selectedFontStyle)} text-sm`}>
              These subtitles are perfectly synced to your video
            </p>
          </div>
        </div>

        {/* Video controls */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button className="text-white">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <span className="text-white text-xs">00:00:05</span>
            </div>
            <div className="flex items-center space-x-3">
              <button className="text-white bg-blue-500 px-2 py-1 rounded text-xs flex items-center">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M18 11a1 1 0 01-1 1h-1.5a1 1 0 01-1-1v-1a1 1 0 011-1H17a1 1 0 011 1v1zM6 11a1 1 0 01-1 1H3.5a1 1 0 01-1-1v-1a1 1 0 011-1H5a1 1 0 011 1v1z"/>
                  <path
                    fillRule="evenodd"
                    d="M2 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm3.5 2a.5.5 0 00-.5.5v3a.5.5 0 001 0v-3a.5.5 0 00-.5-.5zm4 0a.5.5 0 00-.5.5v3a.5.5 0 001 0v-3a.5.5 0 00-.5-.5z"
                    clipRule="evenodd"
                  />
                </svg>
                EN
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Subtitle controls */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subtitle Style
          </label>
          <div className="grid grid-cols-2 gap-3">
            <select
              className="col-span-2 text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-900"
              value={selectedFontStyle}
              onChange={(e) => setSelectedFontStyle(e.target.value)}
            >
              {[
                { label: "Modern Sans", value: "modern-sans" },
                { label: "Classic Serif", value: "classic-serif" },
                { label: "Bold Display", value: "bold-display" },
              ].map((style) => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Text Color
              </label>
              <div className="flex space-x-2">
                {["white", "yellow-400", "cyan-400"].map((color) => (
                  <button
                    key={color}
                    className={
                      `w-6 h-6 rounded-full bg-${color} ` +
                      (selectedColor === color
                        ? "ring-2 ring-primary border-primary"
                        : color === "white"
                        ? "border-2 border-gray-300"
                        : "")
                    }
                    onClick={() => setSelectedColor(color)}
                    aria-label={`Set text color to ${color.replace('-', ' ')}`}
                    type="button"
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Background
              </label>
              <div className="flex space-x-2">
                {[
                  { name: "Red", color: "red-500" },
                  { name: "Yellow", color: "yellow-300" },
                  { name: "Blue", color: "blue-500" },
                ].map(({ name, color }) => (
                  <button
                    key={color}
                    className={
                      `w-6 h-6 rounded-full bg-${color} ` +
                      (selectedBgColor === color
                        ? "ring-2 ring-primary border-primary"
                        : "border-2 border-gray-300")
                    }
                    onClick={() => setSelectedBgColor(color)}
                    aria-label={`Set background color to ${name}`}
                    type="button"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <div className="flex space-x-2">
            <button 
              onClick={() => setSelectedFormat("SRT")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "SRT"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              SRT
            </button>
            <button 
              onClick={() => setSelectedFormat("VTT")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "VTT"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              VTT
            </button>
            <button 
              onClick={() => setSelectedFormat("ASS")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "ASS"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              ASS
            </button>
            <button 
              onClick={() => setSelectedFormat("PDF")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "PDF"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              PDF
            </button>
            <button 
              onClick={() => setSelectedFormat("WEBM")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "WEBM"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              WEBM
            </button>
            <button 
              onClick={() => setSelectedFormat("TXT")}
              className={`px-3 py-1 text-xs rounded flex items-center ${
                selectedFormat === "TXT"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700"
              }`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              TXT
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InteractiveSubtitleDemo;
